import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminMessagesView.css';

const toDate = (v) => (!v ? null : v.toDate ? v.toDate() : new Date(v));
const fmt = (v) => { const d = toDate(v); return d && !isNaN(d) ? d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : ''; };

function AdminMessagesView({ adminData }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [msgCounts, setMsgCounts] = useState(0);
  const endRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'teacherParentConversations'), where('conversationType', '==', 'admin'));
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (toDate(b.lastMessageAt) || 0) - (toDate(a.lastMessageAt) || 0));
      setConversations(list);
      setLoading(false);
    }, (err) => { console.error('Error loading admin conversations:', err); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!selected) { setMessages([]); return undefined; }
    return onSnapshot(collection(db, 'teacherParentConversations', selected.id, 'messages'), (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (toDate(a.sentAt) || 0) - (toDate(b.sentAt) || 0));
      setMessages(list);
      setMsgCounts(list.length);
    });
  }, [selected]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const selectConversation = async (c) => {
    setSelected(c);
    if (c.unreadCount > 0) {
      try { await updateDoc(doc(db, 'teacherParentConversations', c.id), { unreadCount: 0 }); } catch (e) { console.error(e); }
    }
  };

  const stats = useMemo(() => {
    const unread = conversations.filter(c => c.unreadCount > 0).length;
    return {
      total: conversations.length,
      unread,
      unreadMsgs: conversations.reduce((s, c) => s + (c.unreadCount || 0), 0),
      read: conversations.length - unread,
      msgs: selected ? msgCounts : 0
    };
  }, [conversations, msgCounts, selected]);

  const filtered = conversations.filter(c =>
    !search || `${c.parentName} ${c.studentName} ${c.lastMessage}`.toLowerCase().includes(search.toLowerCase()));

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || !selected) return;
    const now = new Date().toISOString();
    const senderRole = (adminData?.role || '').toLowerCase().includes('super') ? 'Super Admin' : 'Admin';
    try {
      // iOS strictly decodes messages — every field must be a present String, and
      // senderRole must be a Title-case UserRole raw value ("Admin"/"Super Admin").
      await addDoc(collection(db, 'teacherParentConversations', selected.id, 'messages'), {
        conversationId: selected.id,
        senderId: adminData?.uid || '',
        senderName: adminData?.displayName || 'Admin',
        senderRole,
        recipientId: selected.parentId || '',
        recipientName: selected.parentName || '',
        message: body,
        studentId: selected.studentId || '',
        studentName: selected.studentName || 'General Inquiry',
        isRead: false,
        sentAt: now
      });
      // Match iOS ChatService.updateTeacherParentConversation: increment unreadCount
      // for the recipient and stamp lastMessageSenderId (ISO string dates).
      await updateDoc(doc(db, 'teacherParentConversations', selected.id), {
        lastMessage: body, lastMessageAt: now, lastMessageSenderId: adminData?.uid || '', unreadCount: increment(1)
      });
      setText('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div><p>Loading messages...</p></div>;

  const cards = [
    ['Conversations', stats.total, 'blue'], ['Unread', stats.unread, 'orange'], ['Unread Msgs', stats.unreadMsgs, 'red'],
    ['Read', stats.read, 'green'], ['Msgs (open chat)', stats.msgs, 'purple']
  ];

  return (
    <div className="admin-messages-view">
      <h2>Messages</h2>
      <div className="am-stats">
        {cards.map(([l, v, c]) => (
          <div key={l} className={`am-stat ${c}`}><div className="am-stat-value">{v}</div><div className="am-stat-label">{l}</div></div>
        ))}
      </div>
      <div className={`am-layout ${selected ? 'has-selection' : ''}`}>
        <div className="am-list">
          <input type="text" placeholder="Search parent, student..." value={search} onChange={(e) => setSearch(e.target.value)} />
          {filtered.length === 0 ? <p className="am-empty">No conversations</p> : filtered.map(c => (
            <div key={c.id} className={`am-item ${selected?.id === c.id ? 'active' : ''}`} onClick={() => selectConversation(c)}>
              <div className="am-item-top"><strong>{c.parentName}</strong>{c.unreadCount > 0 && <span className="am-badge">{c.unreadCount}</span>}</div>
              <small>Re: {c.studentName}</small>
              <p>{c.lastMessage || 'No messages yet'}</p>
              <small>{fmt(c.lastMessageAt)}</small>
            </div>
          ))}
        </div>
        <div className="am-chat">
          {!selected ? <div className="am-empty">Select a conversation</div> : (
            <>
              <div className="am-chat-header">
                <button className="am-back" onClick={() => setSelected(null)}>←</button>
                <div><strong>{selected.parentName}</strong><br /><small>Re: {selected.studentName}</small></div>
              </div>
              <div className="am-messages">
                {messages.map(m => {
                  const roleLC = (m.senderRole || '').toLowerCase().replace(/\s+/g, '');
                  const mine = m.senderId === adminData?.uid || roleLC === 'admin' || roleLC === 'superadmin';
                  return (
                    <div key={m.id} className={`am-msg ${mine ? 'mine' : 'theirs'}`}>
                      <div className="am-msg-body">{m.message}</div>
                      <small>{m.senderName} · {fmt(m.sentAt)}</small>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form className="am-send" onSubmit={send}>
                <input type="text" placeholder="Type a reply..." value={text} onChange={(e) => setText(e.target.value)} />
                <button type="submit" disabled={!text.trim()}>Send</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminMessagesView;
